// Rewriting part of the draft, such as a selection: what's sent, and where its versions go.
import { MAX_CONTEXT_LENGTH, type PassageContext } from '@lyrmo/shared';

/** A part of the draft rewritten on its own, and where it was when it was sent. */
export interface Passage {
  readonly start: number;
  readonly text: string;
  /** The writing around it, so its versions fit back in. */
  readonly context: PassageContext;
}

/** Text at a place in the draft: a passage, or a version put in its place. */
export interface Placed {
  readonly start: number;
  readonly text: string;
}

/** Scripts written without spaces between words. */
export const UNSPACED =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Thai}\p{Script=Lao}\p{Script=Khmer}\p{Script=Myanmar}]/u;

// Part of a word, in writing with spaces between them; apostrophes too, as in "don't".
const inWord = (char: string | undefined) =>
  char !== undefined && /[\p{L}\p{M}\p{N}'’]/u.test(char) && !UNSPACED.test(char);

// The writing on either side, up to the most the API takes, without splitting a character.
const textBefore = (text: string) =>
  text.slice(-MAX_CONTEXT_LENGTH).replace(/^[\uDC00-\uDFFF]/u, '');
const textAfter = (text: string) =>
  text.slice(0, MAX_CONTEXT_LENGTH).replace(/[\uD800-\uDBFF]$/u, '');

/**
 * The selected passage of the draft, to rewrite on its own: whole words, without the spaces at
 * its edges. Null when there's nothing but spaces selected, or when it's all the writing there
 * is, which is rewritten as a whole.
 */
export function selectedPassage(
  text: string,
  selectionStart: number,
  selectionEnd: number,
): Passage | null {
  let start = Math.min(selectionStart, selectionEnd);
  let end = Math.max(selectionStart, selectionEnd);
  while (start < end && /\s/u.test(text.charAt(start))) start += 1;
  while (end > start && /\s/u.test(text.charAt(end - 1))) end -= 1;
  if (start === end) return null;
  while (start > 0 && inWord(text[start - 1]) && inWord(text[start])) start -= 1;
  while (end < text.length && inWord(text[end - 1]) && inWord(text[end])) end += 1;
  const before = text.slice(0, start);
  const after = text.slice(end);
  if (!before.trim() && !after.trim()) return null;
  return {
    start,
    text: text.slice(start, end),
    context: { before: textBefore(before), after: textAfter(after) },
  };
}

/**
 * Where one of `places` is in the draft now, going by its text: the first still where it was,
 * or else the first the draft still has, at the place with its text nearest to where it was.
 * Null when none is left.
 */
export function locate(
  draft: string,
  places: readonly Placed[],
): { start: number; end: number } | null {
  const there = places.find(({ start, text }) => draft.startsWith(text, start));
  if (there) return { start: there.start, end: there.start + there.text.length };
  for (const { start, text } of places) {
    let found: number | null = null;
    for (let at = draft.indexOf(text); at !== -1; at = draft.indexOf(text, at + 1)) {
      if (found === null || Math.abs(at - start) < Math.abs(found - start)) found = at;
    }
    if (found !== null) return { start: found, end: found + text.length };
  }
  return null;
}
